import React, { useEffect } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { useColoredLabelsStore } from '@atlassian/jira-option-color-picker/src/colored-labels-store/index.tsx';
import type { coloredLabelsUpdater_getLabelsInBoardQuery } from '@atlassian/jira-relay/src/__generated__/coloredLabelsUpdater_getLabelsInBoardQuery.graphql';
import { coloredLabelsResource } from '@atlassian/jira-router-resources-colored-labels/src/services/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';

const ColoredLabelsUpdaterInner = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<
		coloredLabelsUpdater_getLabelsInBoardQuery,
		Record<string, unknown>
	>;
}) => {
	const [, { updateColoredLabelSuggestionsMap }] = useColoredLabelsStore();

	const result = usePreloadedQuery<coloredLabelsUpdater_getLabelsInBoardQuery>(
		graphql`
			query coloredLabelsUpdater_getLabelsInBoardQuery($boardId: ID!) {
				jira {
					labelsInBoard(boardId: $boardId) {
						edges {
							node {
								name
								color {
									colorKey
								}
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	useEffect(() => {
		if (!result) return;

		const coloredLabelSuggestionsMap: { [key: string]: string } = {};

		result.jira?.labelsInBoard?.edges?.forEach((labelsNode) => {
			const labelName = labelsNode?.node?.name;
			const labelColor = labelsNode?.node?.color;
			const labelColorKey = labelColor?.colorKey;

			if (
				labelName &&
				!Object.prototype.hasOwnProperty.call(coloredLabelSuggestionsMap, labelName) &&
				labelColorKey
			) {
				coloredLabelSuggestionsMap[labelName] = labelColorKey;
			}
		});

		if (coloredLabelSuggestionsMap) {
			updateColoredLabelSuggestionsMap(coloredLabelSuggestionsMap);
		}
	}, [updateColoredLabelSuggestionsMap, result]);

	return null;
};

export const ColoredLabelsUpdater = () => {
	const { queryReference } = useRelayResource(coloredLabelsResource);

	if (queryReference) {
		return <ColoredLabelsUpdaterInner queryReference={queryReference} />;
	}

	return null;
};
